import { useEffect, useState } from "react";
import AdminLayout from "../layout/AdminLayout";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Campaign } from "../models/campaign";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import { useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import Chip from "@mui/material/Chip";
import { v4 as uuid } from "uuid";
import agent from "../api/agent";
import { toast } from "react-toastify";
import Swal from 'sweetalert2';
import { basename } from "../utils/env";
import { Grid, MenuItem, TextField, Modal, Box, Typography } from "@mui/material";

const gameTypes = [
  {
    "value": 1,
    "name": "Çarkıfelek",
    "image": "spin-and-win"
  },
  {
    "value": 2,
    "name": "Puzzle",
    "image": "puzzle"
  },
  {
    "value": 3,
    "name": "Kazı Kazan",
    "image": "scratch-and-win"
  },
  {
    "value": 4,
    "name": "Dokun Kazan",
    "image": "touch-and-win"
  },
  {
    "value": 5,
    "name": "Eşle Kazan",
    "image": "memory-game"
  },
  {
    "value": 6,
    "name": "Quiz",
    "image": "quiz"
  },
  {
    "value": 7,
    "name": "Düşenleri Yakala",
    "image": "catch-game"
  }
]

export default function ListCampaigns() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedGameType, setSelectedGameType] = useState<number>(0);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const response = await agent.Campaigns.list();
      const fetchedCampaigns = response;
      setCampaigns(fetchedCampaigns);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error('Kampanyalar getirilemedi.');
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  useEffect(() => {
    if (location.state?.reload) {
      fetchCampaigns();
    }
  }, [location.state]);

  const handleGameTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedGameType(parseInt(event.target.value));
  };

  const handleModalOpen = () => {
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleCampaignTypeSelect = (gameType: number) => {
    setModalOpen(false);
    navigate(`/campaigns/new?gameType=${gameType}`);
  };

  const filteredCampaigns = selectedGameType
    ? campaigns.filter((campaign) => campaign.gameType === selectedGameType)
    : campaigns;

  const moveRow = async (id: string, direction: "up" | "down") => {
    try {
      const response = await agent.Campaigns.listOrder(id, direction);
      const index = campaigns.findIndex((campaign) => campaign.id === id);
      if (index === -1) return;

      const newCampaigns = [...campaigns];
      const [movedCampaign] = newCampaigns.splice(index, 1);

      if (direction === "up" && index > 0) {
        newCampaigns.splice(index - 1, 0, movedCampaign);
      } else if (direction === "down" && index < newCampaigns.length) {
        newCampaigns.splice(index + 1, 0, movedCampaign);
      }

      // Update ListOrder
      const updatedCampaigns = newCampaigns.map((campaign, i) => ({
        ...campaign,
        listOrder: i + 1,
      }));

      setCampaigns(updatedCampaigns);

      const reorderedCampaigns = [
        {
          id: movedCampaign.id,
          listOrder: updatedCampaigns.find(c => c.id === movedCampaign.id)!.listOrder
        },
        {
          id:
            direction === "up"
              ? newCampaigns[index].id
              : newCampaigns[index + 1]?.id,
          listOrder:
            direction === "up"
              ? movedCampaign.listOrder
              : newCampaigns[index + 1]?.listOrder
        },
      ];
    } catch (error) {
      toast.error('Sıralama başarısız!');
    }
  };

  const handleDuplicateCampaign = async (id: string) => {
    const {value: campaignTitle} = await Swal.fire({
      title: 'Kampanyayı klonlamak istediğinizden emin misiniz?',
      text: 'Klonlama işlemi için yeni kampanya ismi giriniz',
      icon: 'warning',
      input: "text",
      inputLabel: "Yeni Kampanya İsmi",
      showCancelButton: true,
      confirmButtonText: 'Klonla',
      cancelButtonText: 'İptal',
    });
    
    if (campaignTitle) {
      try {
        const response = await agent.Campaigns.details(id);
        const campaignData: any = response;
        const newCampaignId = uuid();
        const newFormStateId = uuid();
        const newScreenStyleId = uuid();
        const newCampaignData = {
          ...campaignData,
          id: newCampaignId,
          title: campaignTitle,                  
          formState: {
            ...campaignData.formState,
            id: newFormStateId,
            campaignId: newCampaignId
          },
          screenStyle: {
            ...campaignData.screenStyle,
            id: newScreenStyleId,
            campaignId: newCampaignId
          }
        }        
        switch (campaignData.gameType) {
          case 1: { // SpinAndWin
            const newWheelId = uuid();
            campaignData.wheel.slices.sort(
              (a: any, b: any) => a.listOrder - b.listOrder
            );

            newCampaignData.wheel = {
              ...campaignData.wheel,
              id: newWheelId,
              campaignId: newCampaignId,
              slices: campaignData.wheel.slices.map((slice: any) => ({
                ...slice,
                id: uuid(),
                wheelId: newWheelId,
              })),
            };
            break;
          }

          case 2: { // Puzzle
            const newPuzzleId = uuid();
            newCampaignData.puzzle = {
              ...campaignData.puzzle,
              id: newPuzzleId,
              campaignId: newCampaignId,
            };
            break;
          }

          case 3: { // ScratchWin
            const newScratchWinId = uuid();
            newCampaignData.scratchWin = {
              ...campaignData.scratchWin,
              id: newScratchWinId,
              campaignId: newCampaignId,
              scratchPrizes: campaignData.scratchWin.scratchPrizes.map((scratchPrize: any) => ({
                ...scratchPrize,
                id: uuid(),
                wheelId: newScratchWinId,
              })),
            };
            break;
          }

          case 4: { // TouchWin
            const newTouchWinId = uuid();
            newCampaignData.touchWin = {
              ...campaignData.touchWin,
              id: newTouchWinId,
              campaignId: newCampaignId,
            };
            break;
          }

          case 5: { // MatchWin
            const newMatchWinId = uuid();
            newCampaignData.matchWin = {
              ...campaignData.matchWin,
              id: newMatchWinId,
              campaignId: newCampaignId,
              matchObjects: campaignData.matchWin.matchObjects.map((matchObject: any) => ({
                ...matchObject,
                id: uuid(),
                matchWinId: newMatchWinId,
              })),
            };
            break;
          }

          case 6: { // Quiz
            const newQuizId = uuid();
            newCampaignData.quiz = {
              ...campaignData.scratchWin,
              id: newQuizId,
              campaignId: newCampaignId,
              questions: campaignData.quiz.questions.map((question: any) => ({
                ...question,
                id: uuid(),
                quizId: newQuizId,
              })),
            };
            break;
          }

          case 7: { // CatchGame
            const newCatchGameId = uuid();
            newCampaignData.catchGame = {
              ...campaignData.puzzle,
              id: newCatchGameId,
              campaignId: newCampaignId,
            };
            break;
          }

          default:
            console.warn("Unknown game type:", campaignData.gameType);
        }
        try {
          console.log(newCampaignData);
          await agent.Campaigns.create(newCampaignData);
          const refreshCampaigns = await agent.Campaigns.list();
          setCampaigns(refreshCampaigns);
          toast.success('Kampanya başarıyla klonlandı: ' + campaignTitle);
        } catch (error) {
          toast.error('Kampanya klonlanırken bir hata oluştu.');
        }
      } catch (error) {
        toast.error('Kampanya bilgileri okunurken bir hata oluştu.');
      }
    } else {
      toast.error('Klonlamak için yeni kampanya ismi girmelisiniz.');
    }
  };

  const handleDeleteCampaign = (id: string) => {
    Swal.fire({
        title: 'Silmek istediğinizden emin misiniz?',
        text: 'Silme işlemini geri alamazsınız!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Tamam',
        cancelButtonText: 'İptal',
    }).then((result) => {
        if (result.isConfirmed) {
            agent.Campaigns.delete(id).then(() => {
                setCampaigns([...campaigns.filter(x => x.id !== id)]);
                toast.success('Kampanya silindi!');
                setCampaigns([...campaigns.filter(x => x.id !== id)])
            }).catch((error) => {
                toast.error('Kampanya silinirken bir hata oluştu.');
            });
        }
    });
  };

  const columns: GridColDef[] = [
    { field: "id", headerName: "ID", width: 70 },
    { field: "listOrder", headerName: "Sıra", width: 40 },
    { field: "title", headerName: "Kampanya Adı", width: 160 },
    {
      field: "gameType",
      headerName: "Oyun Tipi",
      width: 140,
      renderCell: (params: any) => {
        const gameType = gameTypes.find(gt => gt.value === params.value);
        return (
          <Chip
            label={gameType ? gameType.name : "Bilinmiyor"}
          />
        );
      }
    },
    {
      field: "startDate",
      headerName: "Başlangıç",
      description: "Kampanyanın başlangıç tarihi.",
      width: 140,
      renderCell: (params: any) => dayjs(params.value as string).format("DD.MM.YYYY HH:mm"),
    },
    {
      field: "endDate",
      headerName: "Bitiş",
      description: "Kampanyanın bitiş tarihi.",
      width: 140,
      renderCell: (params: any) => dayjs(params.value as string).format("DD.MM.YYYY HH:mm"),
    },
    {
      field: "isActive",
      headerName: "Yayında Mı?",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? "Yayında" : "Yayında değil"}
          color={params.value ? "success" : "error"}
        />
      ),
    },
    {
      field: "order",
      headerName: "Sıralama",
      width: 100,
      renderCell: (params) => (
        <>
          <IconButton
            onClick={() => moveRow(params.row.id, "up")}
            disabled={params.row.listOrder === 1}
          >
            <ArrowUpwardIcon />
          </IconButton>
          <IconButton
            onClick={() => moveRow(params.row.id, "down")}
            disabled={params.row.listOrder === campaigns.length}
          >
            <ArrowDownwardIcon />
          </IconButton>
        </>
      ),
    },
    {
      field: "actions",
      headerName: "Eylemler",
      width: 360,
      renderCell: (params) => (
        <>
          <Button
            variant="contained"
            size="small"
            color="primary"
            onClick={() => navigate(`/campaigns/${params.row.id}?gameType=${params.row.gameType}`)}
            style={{marginRight: "15px"}}
          >
            Düzenle
          </Button>
          <Button
            variant="contained"
            size="small"
            color="warning"
            onClick={() => window.open(`${basename}/game/${params.row.id}?gameType=${params.row.gameType}`, '_blank')}
            style={{marginRight: "15px"}}
          >
            Oyun
          </Button>
          <Button
            variant="contained"
            size="small"
            color="success"
            onClick={() => handleDuplicateCampaign(params.row.id)}
            style={{marginRight: "15px"}}
          >
            Klonla
          </Button>
          <Button
            variant="contained"
            size="small"
            color="error"
            onClick={() => handleDeleteCampaign(params.row.id)}
          >
            Sil
          </Button>
      </>
      ),
    },
  ];

  return (
    <>
      <AdminLayout title="Kampanyalar">
        <Grid
          container
          spacing={2}
          marginBottom={2}
          sx={{ justifyContent: "flex-start" }}
        >
          <Grid item xs={6} sm={6} md={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleModalOpen}
              sx={{ mb: 2 }}
            >
              Kampanya Ekle
            </Button>
          </Grid>
          <Grid item xs={6} sm={6} md={2}>
            <TextField
              select
              label="Oyun Tipi Seç"
              value={selectedGameType}
              onChange={handleGameTypeChange}
              fullWidth
              variant="outlined"
            >
              <MenuItem value="">
                <em>Tümü</em>
              </MenuItem>
              {gameTypes.map((gameType) => (
                <MenuItem key={gameType.value} value={gameType.value}>
                  {gameType.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
        <DataGrid
          rows={filteredCampaigns}
          columns={columns}
          loading={loading}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 30 },
            },
          }}
          pageSizeOptions={[30, 60]}
          checkboxSelection
        />
        
        {/* Campaign Type Selection Modal */}
        <Modal
          open={modalOpen}
          onClose={handleModalClose}
          aria-labelledby="campaign-type-modal-title"
          aria-describedby="campaign-type-modal-description"
        >
          <div className="game-select-modal">
            <Typography id="campaign-type-modal-title" variant="h6" component="h2" sx={{ mb: 2 }}>
              Oyun Türü Seçin
            </Typography>
            <Typography id="campaign-type-modal-description" sx={{ mb: 2 }}>
              Oluşturmak istediğiniz oyun tipini seçiniz:
            </Typography>
            <ul>
              {gameTypes.map((gameType) => (
                <li key={gameType.value}>
                  <Button onClick={() => handleCampaignTypeSelect(gameType.value)}>
                    <img src={`${process.env.PUBLIC_URL}/assets/game-icons/${gameType.image}.png`} alt={gameType.name} />
                    <h4>{gameType.name}</h4>
                  </Button>
                </li>
              ))}
            </ul>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={handleModalClose} color="secondary">
                İptal
              </Button>
            </Box>
          </div>
        </Modal>
      </AdminLayout>
    </>
  );
}